import { Trans, useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Icon, IconName, IconSize, ProColors } from '../aura';
import { Wave1, Wave5 } from '../aphrodite';
import { FoundationColors } from '../theme';
import { Form, Title } from './templates';

const MAX_JOKES = 10;
const JOKE_DURATION = 4500;

export function StepFinal() {
  const { t } = useTranslation('stepFinal');
  const [currentJoke, setCurrentJoke] = useState(0);
  // from 0 to 100
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentJoke(c => c + 1);
    }, JOKE_DURATION);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(p => {
        if (p < 90) {
          // make it look real
          return p + 2 * Math.random();
        }
        return Math.min(p + 0.1, 100);
      });
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const jokeCount = useMemo(() => {
    for (let i = 1; i <= MAX_JOKES; i += 1) {
      if (
        !t(`joke${i}Setup`, { defaultValue: null }) ||
        !t(`joke${i}Punchline`, { defaultValue: null })
      ) {
        return i - 1;
      }
    }
    return MAX_JOKES;
  }, [t]);

  const displayedJoke = (currentJoke % jokeCount) + 1;

  const jokes: ReactElement[] = [];
  for (let i = 1; i <= jokeCount; i += 1) {
    jokes.push(
      <div
        key={`joke-${i}`}
        css={css({
          position: 'absolute',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          gap: 8,
          opacity: i === displayedJoke ? 1 : 0,
          height: '100%',
          padding: 30,
          transition: 'opacity 0.8s ease-in-out',
        })}
      >
        <h6 className="aura--h6">{t(`joke${i}Setup`)}</h6>
        <div className="aura--copy-normal">{t(`joke${i}Punchline`)}</div>
      </div>,
    );
  }

  return (
    <Form>
      <div css={css({ width: 64, margin: 'auto' })}>
        <Wave5 color={ProColors.yellow[600]} strokeWidth={1.4} />
      </div>
      <Title
        css={css({ textAlign: 'center' })}
        text={
          <Trans
            t={t}
            i18nKey="title"
            transKeepBasicHtmlNodesFor={['br']}
            components={[<span css={css({ color: ProColors.teal[600] })} />]}
          />
        }
      />

      <div
        css={css({
          height: 0,
          borderBottom: `dashed 1px ${FoundationColors.blue[600]}`,
          margin: '12px 0',
          display: 'flex',
        })}
      >
        <div
          css={css({
            transform: 'rotate(45deg) translateX(-12px)',
            marginLeft: `${progress}%`,
            transition: 'margin-left 0.4s ease-in-out',
          })}
        >
          <Icon
            size={IconSize.MEDIUM}
            name={IconName.TW_PRO_HAIR}
            color={ProColors.teal[600]}
          />
        </div>
      </div>

      <h6
        className="aura--h6"
        css={css({ textAlign: 'center', marginBottom: 12 })}
      >
        <Trans
          t={t}
          i18nKey="subTitle"
          transKeepBasicHtmlNodesFor={['br']}
          components={[
            <span css={css({ color: FoundationColors.blue[600] })} />,
          ]}
        />
      </h6>

      <div
        css={css({
          position: 'relative',
          backgroundColor: FoundationColors.blue[100],
          height: 160,
        })}
      >
        <div
          css={css({
            position: 'absolute',
            right: -100,
            bottom: -70,
            transform: 'scaleY(-1) rotate(211deg)',
            width: 240,
            height: 64,
          })}
        >
          <Wave1 color={ProColors.pink[600]} />
        </div>
        {jokes}
      </div>
    </Form>
  );
}
